import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import { PortAreaMatchBadge } from "@/components/common/PortAreaMatch";
import {
  SensitiveContractRef,
  SensitiveMoney,
} from "@/components/common/Sensitive";
import { Badge } from "@/components/ui/badge";
import { buildRateLineDetailUrl } from "@/features/rates/rateLineFilterLink";
import { RateSourceBadge } from "@/features/rates/RateSourceBadge";
import {
  carrierCode,
  sailingEta,
  sailingEtd,
  vesselName,
} from "@/features/schedule/ScheduleLookupCalendarDialog";
import type { AreaMatcher } from "@/lib/portAreaMatchUtils";
import type { MatchedScheduleRates } from "@/lib/scheduleRateMatch";
import { cn } from "@/lib/utils";
import type { RateLineSearchResult, ScheduleResponse } from "@/types/api";
import { formatBookingDate } from "@/types/booking";
import { classifyRateSource, type RateSourceType } from "@/types/rate";

export const SCHEDULE_RATE_FAMILIES: RateSourceType[] = [
  "prepaid",
  "collect",
  "spot",
];

function formatScheduleListDate(
  iso: string | null | undefined,
  compact?: boolean,
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(compact ? {} : { year: "numeric" }),
  });
}

/** Compact rate hints shown left of ETD/ETA on schedule list rows. */
export function SailingRateHints({
  matched,
  compact,
}: {
  matched: MatchedScheduleRates | null | undefined;
  compact?: boolean;
}) {
  if (!matched) return null;
  const families = SCHEDULE_RATE_FAMILIES.filter(
    (fam) => matched.byFamily[fam]?.price_net != null,
  );
  if (families.length === 0) {
    if (matched.best?.price_net == null) return null;
    return (
      <span
        className={cn(
          "shrink-0 text-muted-foreground tabular-nums",
          compact ? "text-[10px]" : "text-[11px]",
        )}
      >
        <SensitiveMoney amount={matched.best.price_net} />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "flex shrink-0 flex-col items-start gap-0.5",
        compact ? "text-[10px]" : "text-[11px]",
      )}
    >
      {families.map((fam) => {
        const line = matched.byFamily[fam]!;
        return (
          <span
            key={fam}
            className="inline-flex items-center gap-1 tabular-nums text-muted-foreground"
            title={
              matched.typeMismatch
                ? "Price for compatible equipment size (type relaxed)"
                : undefined
            }
          >
            <RateSourceBadge
              source={fam}
              className="px-1 py-0 text-[9px]"
            />
            <SensitiveMoney amount={line.price_net!} />
            {matched.typeMismatch && fam === families[0] ? (
              <span className="text-[9px] opacity-70">≈</span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

function equipmentLabel(line: RateLineSearchResult): string {
  const size = line.equipment_size?.trim();
  const type = line.equipment_type?.trim();
  return [size ? `${size}'` : null, type].filter(Boolean).join(" ") || "—";
}

function MatchedRateRow({ line }: { line: RateLineSearchResult }) {
  const href = buildRateLineDetailUrl(line);
  return (
    <li className="rounded-lg border border-border/50 bg-background/40 px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <RateSourceBadge
              source={classifyRateSource(line.card_source, line.is_spot)}
              className="px-1.5 py-0 text-[10px]"
            />
            <span className="font-mono text-xs text-muted-foreground">
              <SensitiveContractRef value={line.contract_ref} />
            </span>
          </span>
          <p className="text-xs text-muted-foreground">
            {equipmentLabel(line)}
            {(line.valid_from || line.valid_to) && (
              <>
                {" · "}
                {formatBookingDate(line.valid_from ?? undefined)}
                {" – "}
                {formatBookingDate(line.valid_to ?? undefined)}
              </>
            )}
          </p>
        </div>
        <span className="shrink-0 text-sm font-semibold tabular-nums">
          {line.price_net != null ? (
            <SensitiveMoney amount={line.price_net} />
          ) : (
            "—"
          )}
        </span>
      </div>
      <Link
        to={href}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        View rate card
        <ArrowUpRight className="size-3.5" />
      </Link>
    </li>
  );
}

/** Full matched-rate block for the sailing detail dialog. */
export function ScheduleSailingMatchedRatesPanel({
  matched,
}: {
  matched: MatchedScheduleRates | null | undefined;
}) {
  if (!matched) return null;

  const lines: RateLineSearchResult[] = [];
  for (const fam of SCHEDULE_RATE_FAMILIES) {
    const line = matched.byFamily[fam];
    if (line?.price_net != null) lines.push(line);
  }
  if (lines.length === 0 && matched.best?.price_net != null) {
    lines.push(matched.best);
  }
  if (lines.length === 0) return null;

  return (
    <section>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Matched rates
      </h3>
      <p className="mb-2 text-[11px] text-muted-foreground">
        Valid on this sailing&apos;s ETD
        {matched.typeMismatch
          ? " · equipment type relaxed to nearest size match"
          : ""}
      </p>
      <ul className="flex flex-col gap-2">
        {lines.map((line) => (
          <MatchedRateRow key={line.id} line={line} />
        ))}
      </ul>
    </section>
  );
}

/** Schedule lookup list row (home widget + calendar day list). */
export function ScheduleSailingListRow({
  sailing,
  areaMatcher,
  compact,
  matchedRates,
  onSelect,
}: {
  sailing: ScheduleResponse;
  areaMatcher: AreaMatcher;
  compact?: boolean;
  matchedRates?: MatchedScheduleRates | null;
  onSelect?: (sailing: ScheduleResponse) => void;
}) {
  const etd = sailingEtd(sailing);
  const eta = sailingEta(sailing);
  const dateBlock = (
    <span
      className={cn(
        "flex flex-col gap-0 leading-tight text-muted-foreground tabular-nums",
        compact ? "text-[10px]" : "text-[11px]",
        compact ? "items-start" : "shrink-0 items-end",
      )}
    >
      <span>ETD {formatScheduleListDate(etd, compact)}</span>
      <span>ETA {formatScheduleListDate(eta, compact)}</span>
      {sailing.transit_time != null && (
        <span>TT {sailing.transit_time}d</span>
      )}
    </span>
  );

  const directBadge = (
    <Badge
      variant={sailing.direct ? "default" : "info"}
      className="shrink-0 px-1.5 py-0 text-[10px]"
    >
      {sailing.direct ? "Direct" : `TS · ${sailing.legs.length}`}
    </Badge>
  );

  return (
    <li>
      <button
        type="button"
        className={cn(
          "flex w-full min-w-0 rounded-md px-1.5 py-1.5 text-left",
          compact ? "flex-col gap-1.5" : "items-center gap-2",
          "hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
        onClick={() => onSelect?.(sailing)}
      >
        <span
          className={cn(
            "flex min-w-0 gap-2",
            compact ? "w-full items-center" : "min-w-0 flex-1 items-center",
          )}
        >
          <CarrierLogo
            carrier={carrierCode(sailing)}
            size={compact ? 18 : 16}
            logoOnly={compact}
            bold
            className="shrink-0 text-xs"
          />
          <span className="flex min-w-0 flex-1 items-center gap-1 truncate text-[11px] text-muted-foreground">
            {vesselName(sailing)}
            <PortAreaMatchBadge
              locode={sailing.origin_locode}
              matcher={areaMatcher}
              className="px-1 py-0 text-[9px]"
            />
          </span>
          {!compact && (
            <>
              <SailingRateHints matched={matchedRates} compact={compact} />
              {dateBlock}
              {directBadge}
            </>
          )}
        </span>
        {compact && (
          <span className="flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-1 border-t border-border/35 pt-1.5">
            <SailingRateHints matched={matchedRates} compact={compact} />
            {dateBlock}
            {directBadge}
          </span>
        )}
      </button>
    </li>
  );
}
